import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslate } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { Input } from 'module/ui/components/atoms/input/input';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Button, Dialog } from '@rocketlane/ui';
import { InputRef } from 'antd';
import { useState, useRef, useEffect, FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const Footer = styled(CenterAlignedBox)`
  justify-content: flex-end;
`;

interface FormValues {
  templateName: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: (payload: { templateName: string }) => Promise<void>;
}

export const PreConfigureTemplateModal: FC<Props> = ({
  open,
  onClose,
  onDone,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [actionLoading, setActionLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const translate = useTranslate();

  useEffect(() => {
    if (open) {
      form.resetFields();
      setIsFormValid(false);
      setActionLoading(false);
      inputRef.current?.focus();
    }
  }, [open, form]);

  const onFormValuesChange = useCallback(() => {
    queueMacrotask(() => {
      form
        .validateFields()
        .then(() => setIsFormValid(true))
        .catch(() => setIsFormValid(false));
    });
  }, [form]);

  const handleNext = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        setActionLoading(true);
        onDone({ templateName: values.templateName.trim() }).finally(() => {
          setActionLoading(false);
        });
      })
      .catch(() => {});
  }, [form, onDone]);

  return (
    <Dialog
      open={open}
      onCancel={onClose}
      title={
        <FormattedMessage id="people_page_template.pre_configure_modal.title" />
      }
      footer={
        <Footer>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={actionLoading}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!isFormValid}
            loading={actionLoading}
          >
            <FormattedMessage id="next" />
          </Button>
        </Footer>
      }
      closable
      overlayClosable={!actionLoading}
      content={
        <Form form={form} layout="vertical" onValuesChange={onFormValuesChange}>
          <Form.Item
            name="templateName"
            label={
              <FormattedMessage id="people_page_template.pre_configure_modal.template_name.label" />
            }
            rules={[
              {
                required: true,
                message: translate('input.required'),
              },
            ]}
            requiredMark={'optional'}
            extra={
              <FormattedMessage id="people_page_template.pre_configure_modal.template_name.helper" />
            }
          >
            <Input
              ref={inputRef}
              placeholder={translate(
                'people_page_template.pre_configure_modal.template_name.placeholder',
              )}
              size="middle"
              autoFocus
              disabled={actionLoading}
            />
          </Form.Item>
        </Form>
      }
    />
  );
};
